import type { LegSkuArming } from '../guaranteed/legModel.ts';
import { useT } from '../../i18n/index.ts';
import { Chip, StatusLamp, Tooltip } from '../../ui/index.ts';
import {
  targetLamp,
  targetLampChip,
  targetLampTip,
  targetLampTone,
  type TargetLegs,
} from './probeDot.ts';

/**
 * One routing target's guaranteed-center state (#2602): the lamp, a chip for
 * a live removal or a shadow verdict, and a hover naming what it means for
 * routing plus the evidence.
 */
export function ProbeStatusDot({
  targetId,
  legs,
  arming,
  nowMs = Date.now(),
}: {
  readonly targetId: string;
  readonly legs: TargetLegs | null;
  readonly arming: LegSkuArming;
  readonly nowMs?: number;
}): JSX.Element {
  const t = useT();
  const serving = legs?.serving ?? null;
  const lamp = targetLamp(serving, arming, nowMs, legs?.member ?? null);
  const tone = targetLampTone(lamp);
  const chip = targetLampChip(lamp, serving);
  const tip = targetLampTip(legs, arming, nowMs, t).join('\n');
  return (
    <Tooltip content={<span className="block whitespace-pre-line">{tip}</span>}>
      <span
        className="inline-flex items-center gap-1"
        title={tip}
        data-testid={`tenant-routing-probe-dot-${targetId}`}
        data-tone={tone}
        data-lamp={lamp}
      >
        <StatusLamp tone={tone} size="sm" />
        {chip !== null ? (
          <Chip
            tone={lamp === 'shadow' ? 'warn' : 'bad'}
            lamp={false}
            testId={`tenant-routing-probe-chip-${targetId}`}
          >
            {t(chip)}
          </Chip>
        ) : null}
      </span>
    </Tooltip>
  );
}
